import React, {FC, useState} from "react"

import {FormattedMessage, getMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import Button from "@irati/ui/src/Button"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import FilterChipToggle from "@irati/ui/src/FilterChipToggle"
import MultiChipsSelect from "@irati/ui/src/MultiChipsSelect"
import Validation from "@irati/utils/validation"
import {OAuthProvider} from "@irati/types"
import {getErrorMessage, getErrorValues} from "@irati/utils/lang/formatError"
import authentication from "@irati/auth"
import {updateOAuthSettings} from "@irati/sso/services/sso"
import ErrorText from "@irati/ui/src/ErrorText"
import adminTracking from "@irati-tracking/admin"
import {ButtonLink} from "@irati/ui/src/Link"

import Step from "./Step"
import OtherSSOModal from "./OtherSSOModal"

const SSO_OTHER = "SSO_selectOther"
const SSO_REMOVE_DOMAIN = "SSO_removeEmailDomain"
const SSO_ADD_DOMAIN = "SSO_addEmailDomain"
const SSO_FINAL_GOOGLE_SETUP = "SSO_finalGoogleSetup"
const SSO_SELECT_GOOGLE = "SSO_selectGoogle"

export interface OAuthSettings {
  provider: OAuthProvider,
  domains: string[]
}

interface Props extends InjectIntl {
  onBack: () => void,
  onClose: (data?: OAuthSettings) => void
}

const EnableOAuth: FC<Props> = ({onBack, onClose, formatMessage}) => {
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [googleOauth, setGoogleOauth] = useState<boolean>(false)
  const [domains, setDomains] = useState<string[]>([])
  const [showModal, setShowModal] = useState<boolean>(false)
  const [disabled, setDisabled] = useState<boolean>(true)
  const [error, setError] = useState<string>()

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    } else if (googleOauth) {
      setError("")
      updateOAuthSettings(authentication.business().uuid, OAuthProvider.GoogleOAuth, domains).then(() => {
        onClose({provider: OAuthProvider.GoogleOAuth, domains})
      }).catch((err) => {
        const message = getMessage(formatMessage, getErrorMessage(err), getErrorValues(err))
        setError(message)
      })
    }
  }

  const handlePrevStep = () => {
    setError("")
    return currentStep === 1 ? onBack() : setCurrentStep(currentStep - 1)
  }

  const handleOtherConnection = () => {
    adminTracking.startIntegrationsSSOJourney(SSO_OTHER)
    setShowModal(true)
    setGoogleOauth(false)
  }

  const handleDomains = (values: string[]) => {
    if (values.length < domains.length) {
      adminTracking.startIntegrationsSSOJourney(SSO_REMOVE_DOMAIN)
    } else {
      adminTracking.startIntegrationsSSOJourney(SSO_ADD_DOMAIN)
    }
    setDomains(values)
    setDisabled(values?.length === 0)
  }

  const toggleGoogleOauth = () => {
    adminTracking.startIntegrationsSSOJourney(SSO_SELECT_GOOGLE)
    setGoogleOauth(true)
  }

  return (
    <>
      <Flex direction="column" width="100%">
        {(() => {
          switch (currentStep) {
            case 1:
              return (
                <Step
                  step={currentStep}
                  title="admin.components.integration.sso.enableSSO.oauth.step1.title"
                  disabled={!googleOauth}
                  onPrev={handlePrevStep}
                  onNext={handleNextStep}>
                  <Flex marginBottom={4}>
                    <FilterChipToggle isToggled={googleOauth} rounded onClick={toggleGoogleOauth} marginRight={2}>
                      <FormattedMessage id="admin.components.integration.sso.enableSSO.oauth.step1.googleBtn" />
                    </FilterChipToggle>
                    <div>
                      <Button marginTop={2} onClick={handleOtherConnection} tertiary>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.oauth.step1.otherBtn" />
                      </Button>
                    </div>
                  </Flex>
                </Step>
              )
            case 2:
              return (
                <Step
                  step={currentStep}
                  title="admin.components.integration.sso.enableSSO.oauth.step2.title"
                  subTitle="admin.components.integration.sso.enableSSO.oauth.step2.desc"
                  disabled={disabled}
                  onPrev={handlePrevStep}
                  onNext={handleNextStep}>
                  <MultiChipsSelect
                    name="domainsMultiChipsSelect"
                    items={domains}
                    onChange={handleDomains}
                    placeholder={formatMessage({id: "admin.components.integration.sso.enableSSO.oauth.step2.addDomains"})}
                    validation={
                      Validation
                        .string()
                        .matches(/^$|^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/,
                          "platform.defaultValidation.domain")} />
                </Step>
              )
            case 3:
              return (
                <>
                  <Step
                    step={currentStep}
                    title="admin.components.integration.sso.enableSSO.oauth.step3.title"
                    subTitle="admin.components.integration.sso.enableSSO.oauth.step3.desc"
                    disabled={false}
                    onPrev={handlePrevStep}
                    onNext={handleNextStep}>
                    <div>
                      <ButtonLink
                        marginBottom={4}
                        href={formatMessage({id: "admin.components.integration.sso.enableSSO.oauth.step3.googleLink"})}
                        target="_blank"
                        onClick={() => adminTracking.startIntegrationsSSOJourney(SSO_FINAL_GOOGLE_SETUP)}>
                        <FormattedMessage id="admin.components.integration.sso.enableSSO.oauth.step3.documentationBtn" />
                        <Icon icon={IconTypes.openTab} marginRight marginLeft />
                      </ButtonLink>
                    </div>
                  </Step>
                  {error && (
                    <Flex justifyContent="flex-end">
                      <ErrorText>{error}</ErrorText>
                    </Flex>
                  )}
                </>
              )
            default:
              return null
          }
        })()}
      </Flex>
      <OtherSSOModal show={showModal} onClose={() => setShowModal(false)}  />
    </>
  )
}

export default injectIntl(EnableOAuth)
